/** Side-nav config builder, driven by typed registry entries and server `visibleNavTags`. */
import type { SideNavItem } from "@performyard/py-components";

import { type NavigationConfig, hasVisibleTag } from "../../api/services/defs/navigation";

import {
	buildPromotionalAdContext,
	getDevelopmentPromotionalUrl,
	getPromotionalUrl,
	shouldShowPromotionalAd,
} from "../Navbar/mainNavConfig";
import {
	ENGAGEMENT_SUBNAV_ORDER,
	FEEDBACK_VIEW_ORDER,
	SIDE_NAV_GROUPS,
	SIDE_NAV_REGISTRY,
	SIDE_NAV_SOURCE_ACTIVE_RULES,
	type SideNavContext,
	type SideNavRegistryItem,
	resolveSideNavRegistryItem,
} from "./side-nav-registry";

type LeafItem = Extract<SideNavItem, { type: "item" }>;

const leaf = (key: keyof typeof SIDE_NAV_REGISTRY, context: SideNavContext): LeafItem =>
	resolveSideNavRegistryItem(SIDE_NAV_REGISTRY[key], context);

const group = (key: keyof typeof SIDE_NAV_GROUPS, children: LeafItem[]): SideNavItem | null => {
	if (children.length === 0) return null;
	return { ...SIDE_NAV_GROUPS[key], type: "group", children };
};

/** Like `group`, but renders as a flat top-level link when only one child is visible. */
const collapsibleGroup = (key: keyof typeof SIDE_NAV_GROUPS, children: LeafItem[]): SideNavItem | null => {
	if (children.length === 1) {
		const { id, label, icon } = SIDE_NAV_GROUPS[key];
		return { id, type: "item", label, href: children[0].href, icon };
	}
	return group(key, children);
};

type RegistryKey = keyof typeof SIDE_NAV_REGISTRY;
type GroupKey = keyof typeof SIDE_NAV_GROUPS;
type Predicate = (config: NavigationConfig) => boolean;

/** A leaf in the declarative tree; visibility defaults to the registry's own `visibility` metadata. */
type LeafDef = { key: RegistryKey; visible?: Predicate };
type GroupDef = { kind: "group"; key: GroupKey; collapse?: boolean; groupVisible?: Predicate; children: LeafDef[] };
type CategoryDef = { kind: "category"; id: string; label: string; visible: Predicate; then: LeafDef };
type PromotionType = "meetings" | "engagement" | "surveys";
type PromoDef = { kind: "promo"; promotionType: PromotionType };
type DevelopmentPromotionModule = "compensation" | "competencies";
type DevelopmentPromoDef = { kind: "development-promo"; module: DevelopmentPromotionModule };
type TopNode = ({ kind: "leaf" } & LeafDef) | GroupDef | CategoryDef | PromoDef | DevelopmentPromoDef;

const isAdminish = (config: NavigationConfig): boolean => config.isAdmin || config.isBasicAdmin;

const directoryPageVisible = (config: NavigationConfig): boolean =>
	hasVisibleTag(config, "company", "directory") || hasVisibleTag(config, "company", "org-chart");

const registryVisibility = (key: RegistryKey): SideNavRegistryItem["visibility"] =>
	(SIDE_NAV_REGISTRY[key] as SideNavRegistryItem).visibility;

/** Default leaf visibility: the registry entry's own area/tag (true when it declares no visibility, e.g. Dashboard). */
const leafVisible = (def: LeafDef, config: NavigationConfig): boolean => {
	if (def.visible) return def.visible(config);
	const visibility = registryVisibility(def.key);
	return visibility ? hasVisibleTag(config, visibility.area, visibility.tag) : true;
};

/**
 * Reviews/Feedback/Competencies children are gated on their parent area being present (under
 * `performance` or `company`) AND their own sub-tag — the parent gate is load-bearing, so it
 * can't be derived from the leaf's registry tag alone.
 */
const blockTagVisible = (
	block: "performance" | "company",
	area: "reviews" | "feedback" | "competencies",
	key: RegistryKey
): Predicate => {
	const tag = registryVisibility(key)?.tag;
	return (config) => hasVisibleTag(config, block, area) && tag !== undefined && hasVisibleTag(config, area, tag);
};

const FEEDBACK_REGISTRY_KEYS = {
	received: "feedbackReceived",
	given: "feedbackGiven",
	team: "feedbackTeam",
	notes: "feedbackNotes",
	requests: "feedbackRequests",
	outgoing: "feedbackOutgoing",
} as const satisfies Record<(typeof FEEDBACK_VIEW_ORDER)[number], RegistryKey>;

const ENGAGEMENT_REGISTRY_KEYS = {
	overview: "engagementOverview",
	cohorts: "engagementCohorts",
	factors: "engagementFactors",
	questions: "engagementQuestions",
	enps: "engagementEnps",
} as const satisfies Record<(typeof ENGAGEMENT_SUBNAV_ORDER)[number], RegistryKey>;

const ENGAGEMENT_GROUP: GroupDef = {
	kind: "group",
	key: "engagement",
	collapse: true,
	children: ENGAGEMENT_SUBNAV_ORDER.map((tag) => ({ key: ENGAGEMENT_REGISTRY_KEYS[tag] })),
};

const SURVEYS_GROUP: GroupDef = {
	kind: "group",
	key: "surveys",
	collapse: true,
	children: [{ key: "mySurveys" }, { key: "allSurveys" }],
};

/**
 * In-app promotion slots: when a customer's current promotion targets a module they don't own, a
 * single "NEW"-badged link is appended after the module group (which is hidden anyway for a
 * non-owner). "Companies with Module" promotions get no nav slot at all — that audience already
 * owns the module, so the promo would duplicate the real group; they reach it through the
 * dashboard banner instead. `tabId` matches the classic navbar's promo helpers so URL + match
 * logic stay single-sourced; `groupKey` supplies the label/icon. Module ownership comes from the
 * config's company feature flags, not visibleNavTags — engagement tags are admin-gated per user,
 * so a basic admin in an engagement-enabled company would otherwise be shown the purchase promo.
 */
const PROMO_MAP = {
	meetings: { tabId: "meeting-workspaces", groupKey: "meetings" },
	engagement: { tabId: "engagement", groupKey: "engagement" },
	surveys: { tabId: "surveys", groupKey: "surveys" },
} as const satisfies Record<PromotionType, { tabId: string; groupKey: keyof typeof SIDE_NAV_GROUPS }>;

const buildPromoItem = (promotionType: PromotionType, config: NavigationConfig): LeafItem[] => {
	const { tabId, groupKey } = PROMO_MAP[promotionType];
	const show = shouldShowPromotionalAd(tabId, buildPromotionalAdContext(config));
	if (!show) return [];
	const promotionId = String(config.currentPromotion?._id ?? "");
	const { id, label, icon } = SIDE_NAV_GROUPS[groupKey];
	return [
		{
			id: `promo-${id}`,
			type: "item",
			label,
			href: getPromotionalUrl(tabId, config.currentUserEmail, promotionId),
			icon,
			badge: "new",
			isExternal: true,
		},
	];
};

const DEVELOPMENT_PROMO_SOURCES = {
	compensation: SIDE_NAV_REGISTRY.compensation,
	competencies: SIDE_NAV_GROUPS.competencies,
} as const satisfies Record<DevelopmentPromotionModule, { id: string; label: string; icon: LeafItem["icon"] }>;

const buildDevelopmentPromoItem = (module: DevelopmentPromotionModule, config: NavigationConfig): LeafItem[] => {
	const show = shouldShowPromotionalAd(module, buildPromotionalAdContext(config));
	if (!show) return [];
	const { id, label, icon } = DEVELOPMENT_PROMO_SOURCES[module];
	return [
		{
			id: `promo-${id}`,
			type: "item",
			label,
			href: getDevelopmentPromotionalUrl(config.currentPromotion, config.currentUserEmail),
			icon,
			badge: "new",
			isExternal: true,
		},
	];
};

const ADMINISTRATION_CATEGORY: CategoryDef = {
	kind: "category",
	id: "administration-cat",
	label: "Administration",
	visible: (config) => isAdminish(config) && hasVisibleTag(config, "administration"),
	then: { key: "companySettings" },
};

/**
 * The full side-nav tree. Adding a future page-set is an entry here, not a new builder function.
 * A leaf with no `visible` falls back to its registry `visibility`; overrides cover the few gates
 * that are more than a single area/tag check (parent-area gates and role-only admin links).
 */
// Competencies slots in after Meetings when that module ships.
const STANDARD_NAV: TopNode[] = [
	{ kind: "leaf", key: "dashboard" },
	{
		kind: "group",
		key: "reviews",
		children: [
			{ key: "formsINeedToDo", visible: blockTagVisible("performance", "reviews", "formsINeedToDo") },
			{ key: "formsIDid", visible: blockTagVisible("performance", "reviews", "formsIDid") },
			{ key: "reviewsOfMe", visible: blockTagVisible("performance", "reviews", "reviewsOfMe") },
			{ key: "reviewsOfMyTeam", visible: blockTagVisible("performance", "reviews", "reviewsOfMyTeam") },
			{ key: "reviewCycles", visible: blockTagVisible("company", "reviews", "reviewCycles") },
			{ key: "allReviews", visible: blockTagVisible("company", "reviews", "allReviews") },
			{ key: "allForms", visible: blockTagVisible("company", "reviews", "allForms") },
			{ key: "assignments", visible: blockTagVisible("company", "reviews", "assignments") },
			{ key: "performanceGraphs", visible: blockTagVisible("company", "reviews", "performanceGraphs") },
		],
	},
	{
		kind: "group",
		key: "goals",
		children: [
			{ key: "myGoals" },
			{
				key: "myTeamGoals",
				visible: (config) => hasVisibleTag(config, "goals") && Boolean(config.hasDirectReports),
			},
			{ key: "companyGoalsList" },
			{ key: "companyGoalsOverview" },
			{ key: "companyGoalsAlignment" },
			{ key: "employeesWithoutGoals" },
		],
	},
	{
		kind: "group",
		key: "competencies",
		children: [
			{ key: "myCompetencies", visible: blockTagVisible("performance", "competencies", "myCompetencies") },
			{
				key: "myTeamCompetencies",
				visible: blockTagVisible("performance", "competencies", "myTeamCompetencies"),
			},
			{ key: "allCompetencies", visible: blockTagVisible("company", "competencies", "allCompetencies") },
		],
	},
	{ kind: "development-promo", module: "competencies" },
	{
		kind: "group",
		key: "meetings",
		groupVisible: (config) => hasVisibleTag(config, "meeting-workspaces"),
		children: [
			{ key: "workspaces" },
			{ key: "actionItems" },
			{
				key: "allMeetings",
				visible: (config) => isAdminish(config) && hasVisibleTag(config, "company", "meetings"),
			},
			{
				key: "meetingCoverage",
				visible: (config) => isAdminish(config) && hasVisibleTag(config, "company", "meetings"),
			},
		],
	},
	{ kind: "promo", promotionType: "meetings" },
	{
		kind: "group",
		key: "feedback",
		children: [
			...FEEDBACK_VIEW_ORDER.map((view) => ({
				key: FEEDBACK_REGISTRY_KEYS[view],
				visible: blockTagVisible("performance", "feedback", FEEDBACK_REGISTRY_KEYS[view]),
			})),
			{
				key: "allFeedback",
				visible: (config) => isAdminish(config) && hasVisibleTag(config, "company", "feedback"),
			},
		],
	},
	SURVEYS_GROUP,
	{ kind: "promo", promotionType: "surveys" },
	{
		kind: "group",
		key: "documents",
		children: [{ key: "myDocuments" }, { key: "companyDocuments" }, { key: "companyLinks" }],
	},
	{
		kind: "group",
		key: "workflows",
		children: [
			{
				key: "allWorkflows",
				visible: (config) => isAdminish(config) && hasVisibleTag(config, "company", "workflows"),
			},
			{
				key: "allTodos",
				visible: (config) => isAdminish(config) && hasVisibleTag(config, "company", "workflows"),
			},
		],
	},
	{
		// Directory and Org-Chart are tabs on one page (PY-13545), so the single nav entry
		// has to survive either surface being switched off — a wider predicate than the
		// registry's own "directory" tag.
		kind: "leaf",
		key: "directory",
		visible: directoryPageVisible,
	},
	{
		kind: "leaf",
		key: "analytics",
		visible: (config) => hasVisibleTag(config, "analytics") || hasVisibleTag(config, "reporting"),
	},
	ENGAGEMENT_GROUP,
	{ kind: "promo", promotionType: "engagement" },
	{ kind: "leaf", key: "compensation" },
	{ kind: "development-promo", module: "compensation" },
	ADMINISTRATION_CATEGORY,
];

/** Engagement-only licenses see a trimmed, reordered tree. */
const ENGAGEMENT_ONLY_NAV: TopNode[] = [
	{ kind: "leaf", key: "dashboard" },
	{ kind: "leaf", key: "directory", visible: directoryPageVisible },
	ENGAGEMENT_GROUP,
	SURVEYS_GROUP,
	ADMINISTRATION_CATEGORY,
];

const buildNode = (node: TopNode, config: NavigationConfig, context: SideNavContext): SideNavItem[] => {
	if (node.kind === "leaf") {
		return leafVisible(node, config) ? [leaf(node.key, context)] : [];
	}
	if (node.kind === "category") {
		if (!node.visible(config)) return [];
		return [{ id: node.id, type: "category", label: node.label }, leaf(node.then.key, context)];
	}
	if (node.kind === "promo") {
		return buildPromoItem(node.promotionType, config);
	}
	if (node.kind === "development-promo") {
		return buildDevelopmentPromoItem(node.module, config);
	}
	if (node.groupVisible && !node.groupVisible(config)) return [];
	const children = node.children
		.filter((child) => leafVisible(child, config))
		.map((child) => leaf(child.key, context));
	const built = node.collapse ? collapsibleGroup(node.key, children) : group(node.key, children);
	return built ? [built] : [];
};

export const calculateSideNavConfig = (config: NavigationConfig): SideNavItem[] => {
	const context = { theUserId: config.theUserId };
	const tree = config.isEngagementOnly ? ENGAGEMENT_ONLY_NAV : STANDARD_NAV;
	return tree.flatMap((node) => buildNode(node, config, context));
};

const parseSearch = (search: string): URLSearchParams => {
	const stripped = search.startsWith("?") ? search.slice(1) : search;
	return new URLSearchParams(stripped);
};

const stripTrailingSlash = (s: string): string => s.replace(/\/$/, "");

const splitHref = (href: string): { path: string; search: string } => {
	const queryIdx = href.indexOf("?");
	if (queryIdx === -1) return { path: href, search: "" };
	return { path: href.slice(0, queryIdx), search: href.slice(queryIdx + 1) };
};

const PATH_LENGTH_WEIGHT = 1_000_000;
const QUERY_MATCH_BONUS = 1_000;
const QUERY_MISS_PENALTY = 1;

const hasItemWithId = (nodes: SideNavItem[], id: string): boolean =>
	nodes.some((node) =>
		node.type === "group" ? hasItemWithId(node.children, id) : node.type === "item" && node.id === id
	);

export const findActiveSideNavKey = (items: SideNavItem[], pathname: string, search = ""): string | null => {
	const targetPath = stripTrailingSlash(pathname);
	const targetParams = parseSearch(search);

	// Honor where the user came from: a detail route can highlight a different nav item when the
	// URL carries an originating `source` (e.g. a workspace opened from Company Meetings).
	const source = targetParams.get("source");
	if (source) {
		const rule = SIDE_NAV_SOURCE_ACTIVE_RULES.find(
			(r) => r.source === source && targetPath.startsWith(`${stripTrailingSlash(r.pathPrefix)}/`)
		);
		if (rule && hasItemWithId(items, rule.itemId)) return rule.itemId;
	}

	let bestId: string | null = null;
	let bestScore = -1;

	const walk = (nodes: SideNavItem[]) => {
		for (const node of nodes) {
			if (node.type === "item") {
				const { path, search: itemSearch } = splitHref(node.href);
				const normalizedPath = stripTrailingSlash(path);
				const itemParams = parseSearch(itemSearch);

				const pathMatches =
					normalizedPath === ""
						? targetPath === ""
						: targetPath === normalizedPath || targetPath.startsWith(`${normalizedPath}/`);
				if (!pathMatches) continue;

				let matched = 0;
				let missed = 0;
				for (const [key, expected] of itemParams.entries()) {
					if (targetParams.get(key) === expected) matched += 1;
					else missed += 1;
				}

				const score =
					normalizedPath.length * PATH_LENGTH_WEIGHT +
					matched * QUERY_MATCH_BONUS -
					missed * QUERY_MISS_PENALTY;
				if (score > bestScore) {
					bestScore = score;
					bestId = node.id;
				}
			} else if (node.type === "group") {
				walk(node.children);
			}
		}
	};
	walk(items);
	return bestId;
};
